import { handleAuthError, isAuthError } from './utils/authUtils';

import authServices from '../main/DependencyInjection';
import { onError } from '@apollo/client/link/error';

const { authService } = authServices;

/**
 * Parses a validation error string in the format "{ field = X, message = Y }"
 * @param validationErrorString - The validation error string to parse
 * @returns Object with field and message, or undefined if parsing fails
 */
const parseValidationErrorString = (validationErrorString: string): { field?: string; message?: string } | undefined => {
  if (typeof validationErrorString !== 'string') {
    return undefined;
  }

  // Match format: "{ field = JobSiteName, message = \"JobSite Name cannot be empty, or it contains invalid characters\" }"
  const match = validationErrorString.match(/\{\s*field\s*=\s*([^,]+),\s*message\s*=\s*([^}]+)\s*\}/);
  if (match) {
    return {
      field: match[1]?.trim(),
      message: match[2]?.trim().replace(/^["']|["']$/g, '') // Remove surrounding quotes
    };
  }

  return undefined;
};

/**
 * Transforms validationErrors from string format to object format for better error handling
 * @param extensions - GraphQL error extensions object
 */
const transformValidationErrors = (extensions: Record<string, unknown>): void => {
  if (extensions?.validationErrors && Array.isArray(extensions.validationErrors)) {
    extensions.validationErrors = extensions.validationErrors.map((ve) => {
      // If it's already an object, return as is
      if (typeof ve === 'object' && ve !== null && 'field' in ve && 'message' in ve) {
        return ve;
      }
      // If it's a string, parse it
      if (typeof ve === 'string') {
        const parsed = parseValidationErrorString(ve);
        if (parsed) {
          return parsed;
        }
        // Fallback: treat entire string as message
        return { message: ve };
      }
      return ve;
    });
  }
};

/**
 * Creates an Apollo error link that handles GraphQL errors, including auth errors
 * This is necessary because GraphQL typically returns 200 OK status codes even for errors,
 * including authorization errors.
 *
 * Also transforms validationErrors from string format to object format for better error handling.
 *
 * @returns Apollo error link middleware
 */
export const createApolloErrorLink = () => {
  return onError(({ graphQLErrors, networkError }) => {
    // Handle GraphQL errors
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        // Transform validationErrors from string format to object format
        if (err.extensions) {
          transformValidationErrors(err.extensions as Record<string, unknown>);
        }

        if (isAuthError(err)) {
          handleAuthError(authService);
          return;
        }

        // Errors are tracked by Sentry automatically, no additional processing needed
      }
    }

    // Handle network errors (including GraphQL errors in networkError.result.errors)
    if (networkError && 'result' in networkError) {
      const networkErr = networkError as { result?: { errors?: Array<{ extensions?: Record<string, unknown> }> } };
      if (networkErr.result?.errors) {
        for (const err of networkErr.result.errors) {
          // Transform validationErrors from string format to object format
          if (err.extensions) {
            transformValidationErrors(err.extensions);
          }
        }
      }
    }
  });
};
